import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { Modal } from 'react-bootstrap';
import { Form } from 'react-final-form';
import { Card, CardBody } from '@spark/ui-components/components/Card';
import DynamicForm from '@spark/ui-components/components/form/DynamicForm';
import { Button, ButtonWithLoading } from '@spark/ui-components/components/Button';
import { FormContainer, FormButtonToolbar } from '@spark/ui-components/components/form/FormElements';
import { notification } from '@spark/ui-components/utils/notification';
import { createPedidoDepot, getPedido } from '@spark/services/pedidos';
import { getOperacoes } from '@spark/services/operacoes';
import { getClientes, getCliente, getModelos, getTiposCarga, getRotas } from '@spark/services';
import { getFormSections, getFormStructure, validateFormDepot } from '../../../../../Pedidos/Cadastro/components/Forms/formConfig';
import ClienteSelectionModal from '../../../../../Clientes/components/ClienteSelectionModal';
import RotaSelectionModal from '../../../../../Pedidos/Cadastro/components/Forms/components/RotaSelectionModal';
import RotaMapModal from '../../../../../Pedidos/Cadastro/components/Forms/components/RotaMapModal';
import TipoCargaSelectionModal from '../../../../../Pedidos/Cadastro/components/Forms/components/TipoCargaSelectionModal';
import OperacaoSelectionModal from '../../../../../Pedidos/Cadastro/components/Forms/components/OperacaoSelectionModal';
import ModeloVeicularSelectionModal from '../../../../../Pedidos/Cadastro/components/Forms/components/ModeloVeicularSelectionModal';
import TomadorSelectionModal from '../../../../../Pedidos/Cadastro/components/Forms/components/TomadorSelectionModal';

// helpers copiados do fluxo principal
const tipoTomadorOptions = [
  { value: '0', label: 'Remetente' },
  { value: '1', label: 'Expedidor' },
  { value: '2', label: 'Recebedor' },
  { value: '3', label: 'Destinatário' },
  { value: '4', label: 'Outros' }
];
const requisitanteOptions = [
  { value: '0', label: 'Remetente' },
  { value: '1', label: 'Destinatário' },
  { value: '2', label: 'Outros' },
  { value: '3', label: 'Redespacho' },
  { value: '4', label: 'Terceiro' },
  { value: '5', label: 'Recebedor' }
];

const getOptionLabel = (options, value) => {
  if (value === null || value === undefined || value === '') return '';
  const found = options.find((option) => String(option.value) === String(value));
  return found ? found.label : String(value);
};

const toApiDateTime = (value) => {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  if (isNaN(d.getTime())) return null;
  const pad = (n) => String(n).padStart(2, '0');
  const yyyy = d.getFullYear();
  const mm = pad(d.getMonth() + 1);
  const dd = pad(d.getDate());
  const hh = pad(d.getHours());
  const mi = pad(d.getMinutes());
  const ss = pad(d.getSeconds());
  return `${yyyy}-${mm}-${dd} ${hh}:${mi}:${ss}`;
};

const toApiDateTimeBr = (value) => {
  if (!value) return null;
  if (typeof value === 'string' && value.includes('/')) return value;
  const d = value instanceof Date ? value : new Date(value);
  if (isNaN(d.getTime())) return null;
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

const parseBrDateTime = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;

  if (typeof value === 'string') {
    if (value.includes('/')) {
      const [datePart, timePart = '00:00:00'] = value.split(' ');
      const [dd, mm, yyyy] = datePart.split('/');
      const [hh = '00', mi = '00', ss = '00'] = timePart.split(':');
      const d = new Date(Number(yyyy), Number(mm) - 1, Number(dd), Number(hh), Number(mi), Number(ss));
      return isNaN(d.getTime()) ? null : d;
    }

    if (value.includes('T')) {
      // ISO sem timezone deve ser tratado como horário local (sem shift)
      const hasTz = /[zZ]|[+-]\d{2}:?\d{2}$/.test(value);
      if (!hasTz) {
        const [datePart, timePart = '00:00:00'] = value.split('T');
        const [yyyy, mm, dd] = datePart.split('-');
        const [hh = '00', mi = '00', ss = '00'] = timePart.replace('Z', '').split(':');
        const d = new Date(Number(yyyy), Number(mm) - 1, Number(dd), Number(hh), Number(mi), Number(ss));
        return isNaN(d.getTime()) ? null : d;
      }
    }
  }

  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d;
};

const extractDigits = (value) => {
  const digits = String(value ?? '').replace(/\D/g, '');
  return digits.length > 0 ? digits : null;
};

const formatCnpj = (value) => {
  const digits = extractDigits(value);
  if (!digits) return '';
  if (digits.length !== 14) return digits;
  return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12)}`;
};

const mapFormToCreateApi = (values, depotId) => {
  const num = (v) => {
    if (v === '' || v === null || v === undefined) return null;
    const s = typeof v === 'string' ? v.replace(',', '.') : v;
    const n = parseFloat(s);
    return isNaN(n) ? null : n;
  };
  const intOrNull = (v) => {
    if (v === '' || v === null || v === undefined) return null;
    const n = parseInt(v, 10);
    return isNaN(n) ? null : n;
  };
  const selectValue = (v) => {
    if (v && typeof v === 'object') {
      return v.value ?? v.Id ?? v.id ?? null;
    }
    return v === '' || v === null || v === undefined ? null : v;
  };
  const extractSelectId = (value) => {
    if (!value || typeof value !== 'object') return null;
    return value.value ?? value.Id ?? value.id ?? null;
  };
  const extractTomadorCnpj = (value) => {
    if (!value) return null;
    if (typeof value === 'object') {
      const raw =
        value.cnpj ??
        value.Cnpj ??
        value.CNPJ ??
        value.label ??
        value.value ??
        value.Nome ??
        value.nome ??
        null;
      return extractDigits(raw);
    }
    return extractDigits(value);
  };
  const extractLabelValue = (value) => {
    if (!value) return null;
    if (typeof value === 'object') {
      return value.label ?? value.value ?? null;
    }
    return value;
  };
  const tipoDeCarga = selectValue(values.tipoDeCarga);
  const tipoDeOperacao = selectValue(values.tipoDeOperacao);
  const modeloVeicularSolicitado = values.modeloVeicularSolicitado?.label || values.modeloVeicularSolicitado;
  const modeloVeicularSolicitadoId = selectValue(values.modeloVeicularSolicitado);
  const requisitante = selectValue(values.requisitante);
  const requisitanteLabel = values.requisitante && typeof values.requisitante === 'object'
    ? values.requisitante.label ?? values.requisitante.value ?? null
    : null;
  const tipoTomador = selectValue(values.tipoTomador);
  const tipoTomadorLabel = values.tipoTomador && typeof values.tipoTomador === 'object'
    ? values.tipoTomador.label ?? values.tipoTomador.value ?? null
    : values.tipoTomador ?? null;
  const duimp = values.duimp ? String(values.duimp).trim() : '';
  const observacaoComplemento = values.observacaoComplemento ? String(values.observacaoComplemento).trim() : '';

  const numeroDoPedido = values.numeroPedidoEmbarcador || values.numeroDoPedido || null;

  return {
    DepotId: depotId,
    NumeroDoPedido: numeroDoPedido,
    IdCarga: null,
    LocalPalletizacao: values.localPalletizacao || '',
    DataAgendamento: toApiDateTime(values.dataAgendamento),
    DataIntegracao: toApiDateTime(new Date()),
    NumeroDePallets: num(values.numeroDePallets),
    PesoLiquido: num(values.pesoLiquido),
    TipoDeRemetente: values.tipoDeRemetente || null,
    NumeroPedidoEmbarcador: values.numeroPedidoEmbarcador || null,
    DataColeta: toApiDateTimeBr(values.dataColeta),
    Remetente: extractLabelValue(values.remetente),
    Destinatario: extractLabelValue(values.destinatario),
    Recebedor: extractLabelValue(values.recebedor),
    Origem: values.origem || null,
    Destino: values.destino || null,
    TipoDeCarga: tipoDeCarga || null,
    TipoDeOperacao: tipoDeOperacao || null,
    EmpresaFilial: extractDigits(values.empresaFilial) || null,
    ModeloVeicularSolicitado: modeloVeicularSolicitado || null,
    DataPrevisaoSaida: toApiDateTime(values.dataPrevisaoSaida),
    DataPrevisaoEntregaRemetente: toApiDateTime(values.dataPrevisaoEntregaRemetente),
    DataInicialViagemExecutada: toApiDateTime(values.dataInicialViagemExecutada),
    DataFinalViagemExecutada: toApiDateTime(values.dataFinalViagemExecutada),
    DataInicialViagemFaturada: toApiDateTime(values.dataInicialViagemFaturada),
    DataFinalViagemFaturada: toApiDateTime(values.dataFinalViagemFaturada),
    NomeTipoTomador: tipoTomadorLabel,
    CodigoTipoRequisitante: requisitante || null,
    NomeTipoRequisitante: requisitanteLabel || null,
    CodigoTipoTomador: tipoTomador !== null && tipoTomador !== undefined && tipoTomador !== '' ? tipoTomador : null,
    PesoDoPedido: num(values.pesoDoPedido),
    QuantidadeDeEntrega: intOrNull(values.quantidadeDeEntrega),
    DataAgendamentoPedido: toApiDateTime(values.dataAgendamentoPedido),
    CondicaoDoPedido: values.condicaoDoPedido || null,
    ObservacaoPedido: values.observacaoPedido || null,
    CubagemPedido: values.CubagemPedido || null,
    ModeloVeicularSolicitadoId: intOrNull(modeloVeicularSolicitadoId),
    Frota: values.frota || null,
    TransportadoraEmitenteId: intOrNull(values.transportadoraEmitenteId),
    RemetenteId: intOrNull(extractSelectId(values.remetente)) || intOrNull(values.remetenteId),
    DestinatarioId: intOrNull(extractSelectId(values.destinatario)) || intOrNull(values.destinatarioId),
    RecebedorId: intOrNull(extractSelectId(values.recebedor)) || intOrNull(values.recebedorId),
    TomadorId: intOrNull(extractSelectId(values.tomador)) || intOrNull(values.tomadorId),
    UtilizarTipoTomadorInformado: true,
    TipoPagamento: selectValue(values.tipoPagamento),
    TipoOperacaoCodigoId: intOrNull(values.tipoOperacaoCodigoId),
    TipoCargaEmbarcadorCodigo: values.tipoCargaEmbarcadorCodigo || null,
    ValorFrete: num(values.valorFrete),
    ValorTotalMercadoria: num(values.valorTotalMercadoria),
    StatusPedidoEmissaoTMS: values.statusPedidoEmissaoTMS || null,
    ProtocoloIntegracaoPedido: values.protocoloIntegracaoPedido || null,
    PesoBruto: num(values.pesoBruto),
    ValorTotalPedido: num(values.valorTotalPedido),
    RotaCodigo: selectValue(values.rota) || values.rotaCodigo || null,
    Tomador: extractTomadorCnpj(values.tomador),
    DUIMP: duimp || null,
    ObservacaoComplemento: observacaoComplemento || null
  };
};

const ValidationToast = ({ submitCount, hasValidationErrors }) => {
  const lastSubmitCountRef = useRef(0);

  useEffect(() => {
    if (hasValidationErrors && submitCount > lastSubmitCountRef.current) {
      notification.error('Preencha os campos obrigatórios.');
      lastSubmitCountRef.current = submitCount;
    }
  }, [submitCount, hasValidationErrors]);

  return null;
};

const PedidoFormModalDepot = ({ show, onClose, depotId, onCreated, duplicatePedidoId }) => {
  const [loading, setLoading] = useState(false);
  const [loadingDuplicate, setLoadingDuplicate] = useState(false);
  const [showRotaMapModal, setShowRotaMapModal] = useState(false);
  const [rotaMapData, setRotaMapData] = useState(null);
  const baseInitialValues = useMemo(
    () => ({
      numeroDoPedido: '',
      localPalletizacao: '',
      dataAgendamento: null,
      numeroDePallets: null,
      pesoLiquido: null,
      tipoDeRemetente: '',
      numeroPedidoEmbarcador: '',
      numeroEmbarcador: '',
      dataColeta: null,
      remetente: '',
      destinatario: '',
      recebedor: '',
      origem: '',
      destino: '',
      tipoDeCarga: '',
      tipoDeOperacao: '',
      rota: '',
      empresaFilial: formatCnpj('03523549000159'),
      modeloVeicularSolicitado: '',
      dataPrevisaoSaida: null,
      dataPrevisaoEntregaRemetente: null,
      dataInicialViagemExecutada: null,
      dataFinalViagemExecutada: null,
      dataInicialViagemFaturada: null,
      dataFinalViagemFaturada: null,
      tomador: '',
      pesoDoPedido: null,
      quantidadeDeEntrega: null,
      dataAgendamentoPedido: null,
      condicaoDoPedido: '',
      observacaoPedido: '',
      tipoTomador: '',
      requisitante: '',
      duimp: '',
      CubagemPedido: '',
      modeloVeicularSolicitadoId: null,
      frota: '',
      transportadoraEmitenteId: null,
      remetenteId: null,
      destinatarioId: null,
      recebedorId: null,
      tomadorId: null,
      tipoTomador: null,
      utilizarTipoTomadorInformado: false,
      tipoPagamento: '',
      tipoOperacaoCodigoId: null,
      tipoCargaEmbarcadorCodigo: '',
      valorFrete: null,
      valorTotalMercadoria: null,
      statusPedidoEmissaoTMS: '',
      protocoloIntegracaoPedido: '',
      pesoBruto: null,
      valorTotalPedido: null,
      rotaCodigo: '',
      observacaoComplemento: ''
    }),
    [],
  );
  const [initialValues, setInitialValues] = useState(baseInitialValues);
  const [formKey, setFormKey] = useState(0);
  const modalComponents = useMemo(
    () => ({
      remetente: ClienteSelectionModal,
      destinatario: ClienteSelectionModal,
      recebedor: ClienteSelectionModal,
      rota: RotaSelectionModal,
      tipoDeCarga: TipoCargaSelectionModal,
      tipoDeOperacao: OperacaoSelectionModal,
      modeloVeicularSolicitado: ModeloVeicularSelectionModal,
      tomador: TomadorSelectionModal
    }),
    []
  );
  const fetchRotaOption = useCallback(async (codigo) => {
    if (!codigo) return null;
    try {
      const response = await getRotas({ CodigoIntegracao: codigo, pagina: 1, quantidadePorPagina: 1 });
      const registros = Array.isArray(response)
        ? response
        : response?.registros || response?.data || [];
      const rota = registros?.[0];
      if (!rota) return null;
      const codigoIntegracao = rota.CodigoIntegracao ?? rota.codigoIntegracao ?? codigo;
      const descricao = rota.Descricao ?? rota.descricao ?? String(codigoIntegracao || codigo);
      const label = descricao && codigoIntegracao && String(descricao) !== String(codigoIntegracao)
        ? `${descricao} (${codigoIntegracao})`
        : descricao || String(codigoIntegracao || codigo);
      return { value: codigoIntegracao, label };
    } catch (error) {
      console.error('Erro ao carregar rota:', error);
      return null;
    }
  }, []);

  const handleOpenRotaMap = useCallback(async (fieldValue) => {
    let codigo =
      (typeof fieldValue === 'object' && (fieldValue.value || fieldValue.CodigoIntegracao || fieldValue.codigoIntegracao)) ||
      fieldValue ||
      null;
    if (typeof codigo === 'object') {
      codigo = codigo.value ?? codigo.CodigoIntegracao ?? codigo.codigoIntegracao ?? null;
    }

    const rotaData =
      (typeof fieldValue === 'object' && (fieldValue.data || fieldValue.rota || fieldValue.Rota)) ||
      null;

    setRotaMapData(rotaData);
    setShowRotaMapModal(true);

    if (rotaData?.Caminho || rotaData?.caminho) {
      return;
    }
    notification.error('Rota sem caminho disponivel para exibir no mapa.');
  }, []);
  const formStructureWithOverrides = useMemo(
    () => getFormStructure({ isDepot: true }).map((row) =>
      row.map((field) => {
        if (field.name === 'numeroDoPedido') {
          return { ...field, disabled: true, readOnly: true };
        }
        if (field.name === 'rota') {
          return {
            ...field,
            extraButtons: [
              {
                id: 'mapa-rota',
                title: 'Ver mapa da rota',
                icon: 'map-marker-path',
                onClick: handleOpenRotaMap
              }
            ]
          };
        }
        return field;
      })
    ),
    [handleOpenRotaMap]
  );

  const generateNumeroPedido = useCallback(() => {
    const d = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    const dd = pad(d.getDate());
    const mm = pad(d.getMonth() + 1);
    const yyyy = d.getFullYear();
    const hh = pad(d.getHours());
    const mi = pad(d.getMinutes());
    const ss = pad(d.getSeconds());
    return `${dd}${mm}${yyyy}${hh}${mi}${ss}`;
  }, []);

  useEffect(() => {
    let isActive = true;

    const loadDuplicate = async () => {
      if (!show) {
        return;
      }

      if (!duplicatePedidoId) {
        const numeroGerado = generateNumeroPedido();
        setInitialValues({
          ...baseInitialValues,
          numeroDoPedido: numeroGerado,
          numeroPedidoEmbarcador: numeroGerado,
          empresaFilial: formatCnpj('03523549000159'),
        });
        setFormKey((k) => k + 1);
        return;
      }

      try {
        setLoadingDuplicate(true);
        const data = await getPedido(duplicatePedidoId);

        const rotaCodigo = data?.RotaCodigo ?? data?.Rota ?? data?.rota?.CodigoIntegracao ?? null;
        let rotaOption = null;
        if (data?.rota) {
          const rotaDescricao = data?.rota?.Descricao ?? data?.rota?.descricao ?? '';
          const rotaCodigoObj = data?.rota?.CodigoIntegracao ?? data?.rota?.codigoIntegracao ?? rotaCodigo;
          const rotaLabel = rotaDescricao && rotaCodigoObj && String(rotaDescricao) !== String(rotaCodigoObj)
            ? `${rotaDescricao} (${rotaCodigoObj})`
            : rotaDescricao || String(rotaCodigoObj || '');
          rotaOption = rotaCodigoObj ? { value: rotaCodigoObj, label: rotaLabel, data: data.rota } : null;
        } else if (rotaCodigo) {
          rotaOption = await fetchRotaOption(rotaCodigo);
        }
        if (!isActive) return;

        let nomeOperacao = data?.operacao?.NomeOperacao || data?.operacao?.nomeOperacao || data?.NomeOperacao || '';
        let tipoDeCargaOption = null;
        let tomadorOption = null;
        let recebedorOption = null;
        let remetenteOption = null;
        let destinatarioOption = null;

        const mapClienteOption = (cliente, fallbackId = null, fallbackLabel = '') => {
          if (!cliente && !fallbackId && !fallbackLabel) return null;
          const id = cliente?.Id ?? cliente?.id ?? fallbackId ?? null;
          const nome = cliente?.Nome ?? cliente?.nome ?? '';
          const cnpjRaw = cliente?.Cnpj ?? cliente?.CNPJ ?? cliente?.cnpj ?? '';
          const cnpjLabel = formatCnpj(cnpjRaw);
          const label = nome && cnpjLabel ? `${nome} - ${cnpjLabel}` : nome || fallbackLabel || String(id || '');
          return { value: id ? String(id) : String(fallbackId || ''), label, cnpj: extractDigits(cnpjRaw) };
        };

        remetenteOption = mapClienteOption(data?.remetenteCliente, data?.RemetenteId ?? null, data?.Remetente || '');
        destinatarioOption = mapClienteOption(data?.destinatarioCliente, data?.DestinatarioId ?? null, data?.Destinatario || '');
        recebedorOption = mapClienteOption(data?.recebedorCliente, data?.RecebedorId ?? null, data?.Recebedor || '');
        tomadorOption = mapClienteOption(data?.tomadorCliente, data?.TomadorId ?? null, data?.Tomador || '');

        if (!nomeOperacao && data?.TipoDeOperacao) {
          try {
            const operacoesResponse = await getOperacoes({ TipoDaOperacao: data.TipoDeOperacao });
            const registros = Array.isArray(operacoesResponse)
              ? operacoesResponse
              : operacoesResponse?.registros || operacoesResponse?.data || [];
            nomeOperacao = registros?.[0]?.NomeOperacao || registros?.[0]?.nomeOperacao || nomeOperacao;
          } catch (error) {
            console.error('Erro ao carregar operacao:', error);
          }
        }

        if (data?.tipoCarga) {
          const value =
            data?.tipoCarga?.CodigoIntegracao ??
            data?.tipoCarga?.codigoIntegracao ??
            data?.tipoCarga?.id ??
            data?.tipoCarga?.Id ??
            data?.TipoDeCarga;
          const label = data?.tipoCarga?.Descricao ?? data?.tipoCarga?.descricao ?? String(value || data?.TipoDeCarga || '');
          tipoDeCargaOption = value ? { value, label } : null;
        }

        if (!tipoDeCargaOption && data?.TipoDeCarga) {
          try {
            const tiposResponse = await getTiposCarga({
              CodigoIntegracao: data.TipoDeCarga,
              quantidadePorPagina: 1
            });
            const registros = Array.isArray(tiposResponse)
              ? tiposResponse
              : tiposResponse?.registros || tiposResponse?.data || [];
            const tipo = registros?.[0];
            if (tipo) {
              const value = tipo.CodigoIntegracao ?? tipo.codigoIntegracao ?? tipo.id ?? tipo.Id ?? data.TipoDeCarga;
              const label = tipo.Descricao ?? tipo.descricao ?? String(value || data.TipoDeCarga);
              tipoDeCargaOption = { value, label };
            }
          } catch (error) {
            console.error('Erro ao carregar tipo de carga:', error);
          }
          if (!tipoDeCargaOption) {
            tipoDeCargaOption = { value: data.TipoDeCarga, label: data.TipoDeCarga };
          }
        }

        const tomadorRaw = data?.Tomador || null;
        const tomadorId = data?.TomadorId ?? null;
        const tomadorCnpj = extractDigits(tomadorRaw);
        const remetenteId = data?.RemetenteId ?? null;
        const destinatarioId = data?.DestinatarioId ?? null;
        const recebedorId = data?.RecebedorId ?? null;

        if (!recebedorOption && recebedorId) {
          try {
            const cliente = await getCliente(recebedorId);
            if (cliente) {
              const clienteId = cliente.Id ?? cliente.id ?? recebedorId;
              const nome = cliente.Nome ?? cliente.nome ?? '';
              recebedorOption = {
                value: String(clienteId),
                label: nome || data.Recebedor || String(recebedorId),
              };
            }
          } catch (error) {
            console.error('Erro ao carregar recebedor:', error);
          }
        }

        if (!tomadorOption && tomadorCnpj) {
          try {
            const clientesResponse = await getClientes({
              Cnpj: tomadorCnpj,
              quantidadePorPagina: 1
            });
            const registros = Array.isArray(clientesResponse)
              ? clientesResponse
              : clientesResponse?.registros || clientesResponse?.data || [];
            const cliente = registros?.[0];
            if (cliente) {
              const nome = cliente.Nome ?? cliente.nome ?? '';
              const cnpj = cliente.CNPJ ?? cliente.Cnpj ?? cliente.cnpj ?? '';
              const id = cliente.Id ?? cliente.id ?? null;
              const label = nome && cnpj ? `${nome} - ${cnpj}` : nome || cnpj || String(cnpj || tomadorRaw);
              tomadorOption = { value: id ?? tomadorId ?? null, label, cnpj };
            }
          } catch (error) {
            console.error('Erro ao carregar tomador:', error);
          }
        }

        if (!tomadorOption && tomadorRaw && tomadorId) {
          tomadorOption = {
            value: tomadorId,
            label: tomadorRaw,
            cnpj: tomadorCnpj
          };
        } else if (!tomadorOption && tomadorRaw) {
          tomadorOption = { value: tomadorId ?? tomadorRaw, label: tomadorRaw, cnpj: tomadorCnpj };
        }

        const tipoTomadorCodigo =
          data.CodigoTipoTomador ?? data.codigoTipoTomador ?? data.TipoTomador ?? data.tipoTomador ?? null;
        const tipoTomadorLabel = getOptionLabel(tipoTomadorOptions, tipoTomadorCodigo);
        const requisitanteCodigo =
          data.CodigoTipoRequisitante ?? data.codigoTipoRequisitante ?? data.Requisitante ?? data.requisitante ?? null;
        const requisitanteLabel =
          data.NomeTipoRequisitante ?? data.nomeTipoRequisitante ?? getOptionLabel(requisitanteOptions, requisitanteCodigo);
        const numeroGerado = generateNumeroPedido();
        setInitialValues({
          ...baseInitialValues,
          numeroDoPedido: numeroGerado,
          localPalletizacao: data.LocalPalletizacao || '',
          dataAgendamento: data.DataAgendamento ? new Date(data.DataAgendamento) : null,
          numeroDePallets: data.NumeroDePallets ?? null,
          pesoLiquido: data.PesoLiquido ?? null,
          tipoDeRemetente: data.TipoDeRemetente || '',
          numeroPedidoEmbarcador: numeroGerado,
          numeroEmbarcador: data.NumeroEmbarcador || '',
          dataColeta: parseBrDateTime(data.DataColeta),
          remetente: remetenteId
            ? remetenteOption || { value: String(remetenteId), label: data.Remetente || String(remetenteId) }
            : data.Remetente || '',
          destinatario: destinatarioId
            ? destinatarioOption || { value: String(destinatarioId), label: data.Destinatario || String(destinatarioId) }
            : data.Destinatario || '',
          recebedor: recebedorId
            ? recebedorOption || { value: String(recebedorId), label: data.Recebedor || String(recebedorId) }
            : data.Recebedor || '',
          origem: data.Origem || '',
          destino: data.Destino || '',
          tipoDeCarga: data.TipoDeCarga ? tipoDeCargaOption || { value: data.TipoDeCarga, label: data.TipoDeCarga } : '',
          tipoDeOperacao: data.TipoDeOperacao
            ? { value: data.TipoDeOperacao, label: nomeOperacao || data.TipoDeOperacao }
            : '',
          rota: rotaOption || data.RotaCodigo || data.Rota || '',
          empresaFilial: formatCnpj(data.EmpresaFilial || '03523549000159'),
          modeloVeicularSolicitado: data.ModeloVeicularSolicitadoId || data.ModeloVeicularSolicitado
            ? {
              value: data.ModeloVeicularSolicitadoId || data.ModeloVeicularSolicitado,
              label:
                data.ModeloVeicularSolicitado ||
                data.ModeloVeicular?.Placa ||
                data.ModeloVeicular?.Descricao ||
                data.ModeloVeicular?.NomeTipoModeloVeicular ||
                data.ModeloVeicularSolicitadoId
            }
            : '',
          dataPrevisaoSaida: data.DataPrevisaoSaida ? new Date(data.DataPrevisaoSaida) : null,
          dataPrevisaoEntregaRemetente: data.DataPrevisaoEntregaRemetente ? new Date(data.DataPrevisaoEntregaRemetente) : null,
          dataInicialViagemExecutada: data.DataInicialViagemExecutada ? new Date(data.DataInicialViagemExecutada) : null,
          dataFinalViagemExecutada: data.DataFinalViagemExecutada ? new Date(data.DataFinalViagemExecutada) : null,
          dataInicialViagemFaturada: data.DataInicialViagemFaturada ? new Date(data.DataInicialViagemFaturada) : null,
          dataFinalViagemFaturada: data.DataFinalViagemFaturada ? new Date(data.DataFinalViagemFaturada) : null,
          tomador: tomadorRaw
            ? tomadorOption || { value: tomadorId ?? tomadorRaw, label: tomadorRaw, cnpj: tomadorCnpj }
            : '',
          tipoTomador: tipoTomadorCodigo !== '' && tipoTomadorCodigo !== null && tipoTomadorCodigo !== undefined
            ? { value: String(tipoTomadorCodigo), label: tipoTomadorLabel }
            : '',
          requisitante: requisitanteCodigo !== '' && requisitanteCodigo !== null && requisitanteCodigo !== undefined
            ? { value: String(requisitanteCodigo), label: requisitanteLabel }
            : '',
          pesoDoPedido: data.PesoDoPedido ?? null,
          quantidadeDeEntrega: data.QuantidadeDeEntrega ?? null,
          dataAgendamentoPedido: data.DataAgendamentoPedido ? new Date(data.DataAgendamentoPedido) : null,
          condicaoDoPedido: data.CondicaoDoPedido || '',
          observacaoPedido: data.ObservacaoPedido || '',
          duimp: data.DUIMP ?? data.Duimp ?? '',
          observacaoComplemento: data.ObservacaoComplemento || '',
          CubagemPedido: data.CubagemPedido || '',
          valorFrete: data.ValorFrete ?? data.valorFrete ?? null,
          valorTotalMercadoria: data.ValorTotalMercadoria ?? null,
          remetenteId,
          destinatarioId,
          recebedorId,
        });
        setFormKey((k) => k + 1);
      } catch (error) {
        console.error('Erro ao carregar pedido para duplicar:', error);
        const numeroGerado = generateNumeroPedido();
        setInitialValues({
          ...baseInitialValues,
          numeroDoPedido: numeroGerado,
        });
        setFormKey((k) => k + 1);
      } finally {
        if (isActive) {
          setLoadingDuplicate(false);
        }
      }
    };

    loadDuplicate();

    return () => {
      isActive = false;
    };
  }, [show, duplicatePedidoId, baseInitialValues, generateNumeroPedido]);

  const handleSearchTiposCarga = useCallback(async (searchTerm) => {
    try {
      const tipos = await getTiposCarga({ Descricao: searchTerm });
      const registros = Array.isArray(tipos)
        ? tipos
        : tipos?.registros || tipos?.data || [];
      return registros.map((tipo) => {
        const value = tipo.CodigoIntegracao ?? tipo.codigoIntegracao ?? tipo.id ?? tipo.Id ?? '';
        const label = tipo.Descricao ?? tipo.descricao ?? String(value || '');
        return { value, label };
      });
    } catch (error) {
      console.error('Erro ao buscar tipos de carga:', error);
      return [];
    }
  }, []);
  const handleSearchOperacoes = useCallback(async (searchTerm) => {
    try {
      const operacoes = await getOperacoes({ NomeOperacao: searchTerm });
      const registros = Array.isArray(operacoes)
        ? operacoes
        : operacoes?.registros || operacoes?.data || [];
      return registros.map((operacao) => ({
        value: operacao.TipoDaOperacao ?? operacao.tipoDaOperacao ?? '',
        label: operacao.NomeOperacao ?? operacao.nomeOperacao ?? ''
      }));
    } catch (error) {
      console.error('Erro ao buscar operacoes:', error);
      return [];
    }
  }, []);
  const handleSearchModelos = useCallback(async (searchTerm) => {
    try {
      const modelos = await getModelos({ search: searchTerm, limit: 30 });
      const registros = Array.isArray(modelos)
        ? modelos
        : modelos?.registros || modelos?.data || [];
      return registros.map((modelo) => ({
        value: modelo.id ?? modelo.Id ?? '',
        label:
          modelo.descricao ??
          modelo.Descricao ??
          modelo.nomeTipoModeloVeicular ??
          modelo.NomeTipoModeloVeicular ??
          String(modelo.id ?? modelo.Id ?? '')
      }));
    } catch (error) {
      console.error('Erro ao buscar modelos veiculares:', error);
      return [];
    }
  }, []);
  const handleSearchTomador = useCallback(async (searchTerm) => {
    try {
      const clientes = await getClientes({ CNPJ: searchTerm, quantidadePorPagina: 30 });
      const registros = Array.isArray(clientes)
        ? clientes
        : clientes?.registros || clientes?.data || [];
      return registros
        .map((cliente) => {
          const nome = cliente.Nome ?? cliente.nome ?? '';
          const cnpj = cliente.CNPJ ?? cliente.Cnpj ?? cliente.cnpj ?? '';
          const id = cliente.Id ?? cliente.id ?? '';
          const label = nome && cnpj ? `${nome} - ${cnpj}` : nome || cnpj || String(id || '');
          return { value: id, label, cnpj };
        })
        .filter((item) => item.value || item.label);
    } catch (error) {
      console.error('Erro ao buscar tomadores:', error);
      return [];
    }
  }, []);

  const onSubmit = useCallback(
    async (values) => {
      try {
        setLoading(true);
        const payload = mapFormToCreateApi(values, depotId);
        const response = await createPedidoDepot(payload);
        const tms = response?.tms ?? response?.data?.tms ?? null;
        const tmsMessage = tms?.message || tms?.mensagem || '';
        if (tms && tms.success === false) {
          notification.error(
            tmsMessage || 'Falha ao enviar para o TMS',
            'Erro ao criar pedido'
          );
          return;
        } else if (response?.message || response?.mensagem) {
          notification.success(response.message || response.mensagem);
        } else {
          notification.success('Pedido criado com sucesso!');
        }
        if (onCreated) onCreated();
        onClose();
      } catch (err) {
        const errMessage =
          err?.response?.data?.message ||
          err?.response?.data?.mensagem ||
          err?.message ||
          'Erro ao criar pedido';
        notification.error(errMessage, 'Erro ao criar pedido');
      } finally {
        setLoading(false);
      }
    },
    [depotId, onClose, onCreated],
  );

  const handleCancel = useCallback(() => {
    if (loading) return;
    onClose();
  }, [loading, onClose]);

  return (
    <Modal show={show} onHide={handleCancel} size="xl" backdrop="static" centered>
      <Modal.Header closeButton>
        <Modal.Title>{duplicatePedidoId ? 'Duplicar Pedido (Depot)' : 'Novo Pedido (Depot)'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loadingDuplicate ? (
          <div style={{ textAlign: 'center', padding: '24px' }}>
            Carregando dados do pedido...
          </div>
        ) : (
          <Form onSubmit={onSubmit} initialValues={initialValues} validate={validateFormDepot} key={formKey}>
            {({ handleSubmit, submitCount, hasValidationErrors }) => (
              <Card>
                <CardBody>
                  <ValidationToast
                    submitCount={submitCount}
                    hasValidationErrors={hasValidationErrors}
                  />
                  <FormContainer onSubmit={handleSubmit}>
                    <DynamicForm
                      structure={formStructureWithOverrides}
                      sections={getFormSections(formStructureWithOverrides, initialValues, { isDepot: true })}
                      searchFunctions={{
                        tipoDeCarga: handleSearchTiposCarga,
                        tipoDeOperacao: handleSearchOperacoes,
                        modeloVeicularSolicitado: handleSearchModelos,
                        tomador: handleSearchTomador
                      }}
                      modalComponents={modalComponents}
                    />
                    <RotaMapModal
                      show={showRotaMapModal}
                      onHide={() => setShowRotaMapModal(false)}
                      rota={rotaMapData}
                    />
                    <FormButtonToolbar className="mt-4">
                      <ButtonWithLoading variant="primary" type="submit" loading={!!loading}>
                        Salvar
                      </ButtonWithLoading>
                      <Button variant="secondary" type="button" onClick={handleCancel}>
                        Cancelar
                      </Button>
                    </FormButtonToolbar>
                  </FormContainer>
                </CardBody>
              </Card>
            )}
          </Form>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default PedidoFormModalDepot;



